import {
  mergeAttributes,
  Node,
  NodeViewWrapper,
  ReactNodeViewRenderer
} from '@tiptap/react';
import { CheckIcon, ChevronsUpDownIcon } from 'lucide-react';
import { type ComponentProps, useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface Attributes {
  id: string;
  isReadOnly: boolean;
  options: {
    label: string;
    value: string;
  }[];
  placeholder: string;
  value: null | string;
}

const ComboBoxFieldView: Parameters<typeof ReactNodeViewRenderer>[0] = ({
  node,
  selected,
  updateAttributes
}) => {
  const { isReadOnly, options, placeholder, value } = node.attrs as Attributes;

  const triggerRef = useRef<HTMLButtonElement>(null);

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!selected || !triggerRef.current) return;

    triggerRef.current.focus();
  }, [selected]);

  const handleSelect: NonNullable<
    ComponentProps<typeof CommandItem>['onSelect']
  > = currentValue => {
    updateAttributes({
      value: currentValue === value ? '' : currentValue
    });
    setOpen(false);
  };

  return (
    <NodeViewWrapper as="span">
      <Popover
        onOpenChange={setOpen}
        open={open}
      >
        <PopoverTrigger asChild>
          <Button
            aria-expanded={open}
            className="h-8 justify-between rounded-sm px-2!"
            disabled={isReadOnly}
            ref={triggerRef}
            role="combobox"
            tabIndex={-1}
            variant="outline"
          >
            {value ? (
              options.find(option => option.value === value)?.label
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
            <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto min-w-[var(--radix-popover-trigger-width)] p-0">
          <Command>
            <CommandInput placeholder={placeholder} />
            <CommandList>
              <CommandEmpty>No item found.</CommandEmpty>
              <CommandGroup>
                {options.map(option => (
                  <CommandItem
                    key={option.value}
                    onSelect={handleSelect}
                    value={option.value}
                  >
                    <CheckIcon
                      className={cn(
                        'mr-2 h-4 w-4',
                        value === option.value ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </NodeViewWrapper>
  );
};

const ComboBox = Node.create({
  addAttributes() {
    return {
      id: {
        default: '',
        parseHTML: element => element.getAttribute('data-id'),
        renderHTML: (attrs: Attributes) => ({ 'data-id': attrs.id })
      },
      isReadOnly: {
        default: false,
        parseHTML: element => element.getAttribute('data-is-read-only'),
        renderHTML: (attrs: Attributes) => ({
          'data-is-read-only': attrs.isReadOnly
        })
      },
      options: {
        default: [],
        parseHTML: element => {
          const json = element.getAttribute('data-options');
          try {
            return json ? (JSON.parse(json) as Attributes['options']) : [];
          } catch {
            return [];
          }
        },
        renderHTML: (attrs: Attributes) => ({
          'data-options': JSON.stringify(attrs.options)
        })
      },
      placeholder: {
        default: null,
        parseHTML: element => element.getAttribute('data-placeholder'),
        renderHTML: (attrs: Attributes) => ({
          'data-placeholder': attrs.placeholder
        })
      },
      value: {
        default: null,
        parseHTML: element => element.getAttribute('data-value'),
        renderHTML: (attrs: Attributes) => ({
          'data-value': attrs.value ?? ''
        })
      }
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(ComboBoxFieldView);
  },
  atom: true,
  group: 'inline',
  inline: true,
  name: 'comboBoxField',
  parseHTML() {
    return [{ tag: 'span[data-combo-box-field]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, { 'data-combo-box-field': '' }),
      0
    ];
  }
});

export default ComboBox;
